import React, { useCallback, useEffect, useState, useRef, Fragment } from 'react';
import { TypeIcon, HeadingIcon, MousePointerIcon, ImageIcon, PlayIcon, MinusIcon, MoveVerticalIcon, ColumnsIcon, Share2Icon, UserMinusIcon, LayoutIcon, SquareIcon, TableIcon, CodeIcon, FootprintsIcon, UndoIcon, RedoIcon, PaletteIcon, ChevronRightIcon, TagIcon, XIcon, WandSparklesIcon, Loader2Icon, SendIcon, PlusIcon, ChevronDownIcon, SettingsIcon, CopyIcon, Trash2Icon, GripVerticalIcon, SearchIcon, ImagePlusIcon, SlidersHorizontalIcon, SparklesIcon } from 'lucide-react';
import { EmailBlock, EmailBlockType, EmailGlobalStyles, DEFAULT_EMAIL_STYLES, EMAIL_PALETTE, createEmailBlock, generateId } from './types';
import { generateEmailHtml } from './emailHtmlGenerator';
import { callGPTAPI } from '../../utils/api';
import { mediaLibraryApi, MediaLibraryRecord } from '../../services/creatorApi';
import { useWorkspace } from '../../contexts/WorkspaceContext';
const ICONS: Record<string, React.ElementType> = {
  Type: TypeIcon,
  Heading: HeadingIcon,
  MousePointer: MousePointerIcon,
  Image: ImageIcon,
  Play: PlayIcon,
  Minus: MinusIcon,
  MoveVertical: MoveVerticalIcon,
  Columns: ColumnsIcon,
  Share2: Share2Icon,
  UserMinus: UserMinusIcon,
  Layout: LayoutIcon,
  Square: SquareIcon,
  Table: TableIcon,
  Code: CodeIcon,
  Footprints: FootprintsIcon
};
const SOCIAL_PLATFORMS = ['facebook', 'twitter', 'instagram', 'linkedin', 'youtube', 'tiktok', 'pinterest', 'whatsapp'];
const EMAIL_BEAUTIFY_INSTRUCTIONS = `You are an expert email HTML developer and designer. You receive an email's complete HTML. The user will give you a specific instruction about what to change.

RULES:
1. Return ONLY an HTML code block with the complete email HTML.
2. Keep ALL merge variables like {{confirmation_link}}, {{team_name}}, {{first_name}} etc.
3. Keep the TABLE-BASED layout for email client compatibility.
4. Use inline styles (email clients strip style blocks).
5. CRITICAL: You must ONLY change the specific things the user asked for. Leave ALL other styles, content, and structure exactly as they are.
6. If the user says "make the button green", ONLY change the button color. Do NOT touch fonts, backgrounds, spacing, or anything else.
7. If the user says "change the heading font size", ONLY change that. Do NOT redesign the entire email.
8. Start with the EXACT current HTML and make MINIMAL targeted changes based on the user's instruction.
9. Keep it responsive with the existing @media queries.
10. Do NOT add any explanation — ONLY the html code block:
\`\`\`html
(your modified email HTML here)
\`\`\`
11. If no specific instruction is given, make subtle improvements while keeping the overall look intact — do NOT redesign from scratch.`;
function parseHtmlFromResponse(response: string): string {
  const htmlMatch = response.match(/```html\s*\n([\s\S]*?)```/i);
  if (htmlMatch) return htmlMatch[1].trim();
  const genericMatch = response.match(/```\s*\n([\s\S]*?)```/i);
  if (genericMatch) return genericMatch[1].trim();
  return response.trim();
}
// Types that can be placed inside columns
const COLUMN_ALLOWED_TYPES: EmailBlockType[] = ['text', 'heading', 'button', 'image', 'divider', 'spacer', 'boxed_text', 'html_block', 'image_text', 'table'];
interface EmailBuilderProps {
  initialBlocks?: EmailBlock[];
  initialStyles?: EmailGlobalStyles;
  onSchemaChange: (blocks: EmailBlock[], styles: EmailGlobalStyles) => void;
  onHtmlChange: (html: string) => void;
  mergeVariables?: string[];
}
export function EmailBuilder({
  initialBlocks,
  initialStyles,
  onSchemaChange,
  onHtmlChange,
  mergeVariables = []
}: EmailBuilderProps) {
  const [blocks, setBlocks] = useState<EmailBlock[]>(initialBlocks || []);
  const [styles, setStyles] = useState<EmailGlobalStyles>(initialStyles || DEFAULT_EMAIL_STYLES);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Track selected child block inside a column: { parentId, colIndex, childId }
  const [selectedChild, setSelectedChild] = useState<{
    parentId: string;
    colIndex: number;
    childId: string;
  } | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  // Track drag over column: { parentId, colIndex, position }
  const [dragOverCol, setDragOverCol] = useState<{
    parentId: string;
    colIndex: number;
    position: number;
  } | null>(null);
  const [history, setHistory] = useState<EmailBlock[][]>([initialBlocks || []]);
  const [historyIdx, setHistoryIdx] = useState(0);
  const [collapsedCats, setCollapsedCats] = useState<Set<string>>(new Set());
  const [isBeautifying, setIsBeautifying] = useState(false);
  const [beautifyPrompt, setBeautifyPrompt] = useState('');
  const [showBeautifyInput, setShowBeautifyInput] = useState(false);
  const [sidebarTab, setSidebarTab] = useState<'elements' | 'layouts' | 'design'>('elements');
  // Track HTML before beautify so undo can revert it
  const [preBeautifyHtml, setPreBeautifyHtml] = useState<string | null>(null);
  // Column configuration modal state
  const [columnConfigModal, setColumnConfigModal] = useState<{
    isOpen: boolean;
    blockId: string;
    currentRatio: string;
  } | null>(null);
  const dragItemRef = useRef<{
    type: 'palette' | 'canvas';
    blockType?: EmailBlockType;
    index?: number;
  } | null>(null);
  // Find the selected block — could be top-level or a child inside a column
  const selectedBlock = (() => {
    if (selectedChild) {
      const parent = blocks.find(b => b.id === selectedChild.parentId);
      if (parent?.columns?.[selectedChild.colIndex]) {
        return parent.columns[selectedChild.colIndex].find(cb => cb.id === selectedChild.childId) || null;
      }
      return null;
    }
    return blocks.find(b => b.id === selectedId) || null;
  })();
  const pushHistory = useCallback((newBlocks: EmailBlock[]) => {
    setHistory(prev => [...prev.slice(0, historyIdx + 1), newBlocks].slice(-50));
    setHistoryIdx(prev => Math.min(prev + 1, 49));
  }, [historyIdx]);
  const updateBlocks = useCallback((newBlocks: EmailBlock[]) => {
    setBlocks(newBlocks);
    pushHistory(newBlocks);
    const html = generateEmailHtml(newBlocks, styles);
    onSchemaChange(newBlocks, styles);
    onHtmlChange(html);
  }, [styles, pushHistory, onSchemaChange, onHtmlChange]);
  useEffect(() => {
    // Only sync generated HTML when the builder has blocks.
    // This prevents empty builder initialization from clearing the code editor.
    if (blocks.length > 0) {
      const html = generateEmailHtml(blocks, styles);
      onSchemaChange(blocks, styles);
      onHtmlChange(html);
    }
  }, [styles]);
  const undo = useCallback(() => {
    // If we have a beautify HTML to revert, do that first
    if (preBeautifyHtml !== null) {
      onHtmlChange(preBeautifyHtml);
      setPreBeautifyHtml(null);
      return;
    }
    if (historyIdx > 0) {
      const newIdx = historyIdx - 1;
      setHistoryIdx(newIdx);
      const prev = history[newIdx];
      setBlocks(prev);
      onSchemaChange(prev, styles);
      onHtmlChange(generateEmailHtml(prev, styles));
    }
  }, [historyIdx, history, styles, onSchemaChange, onHtmlChange, preBeautifyHtml]);
  const redo = useCallback(() => {
    if (historyIdx < history.length - 1) {
      const newIdx = historyIdx + 1;
      setHistoryIdx(newIdx);
      const next = history[newIdx];
      setBlocks(next);
      onSchemaChange(next, styles);
      onHtmlChange(generateEmailHtml(next, styles));
    }
  }, [historyIdx, history, styles, onSchemaChange, onHtmlChange]);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        e.shiftKey ? redo() : undo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo]);
  const handlePaletteDragStart = (type: EmailBlockType) => (e: React.DragEvent) => {
    dragItemRef.current = {
      type: 'palette',
      blockType: type
    };
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', type); // needed for column drops
  };
  const handleCanvasDragStart = (index: number) => (e: React.DragEvent) => {
    dragItemRef.current = {
      type: 'canvas',
      index
    };
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverIdx(index);
    setDragOverCol(null);
  };
  const handleBlockDragOver = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const dropIdx = e.clientY < midY ? index : index + 1;
    e.dataTransfer.dropEffect = dragItemRef.current?.type === 'palette' ? 'copy' : 'move';
    setDragOverIdx(dropIdx);
    setDragOverCol(null);
  };
  const handleBlockDrop = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const dropIdx = e.clientY < midY ? index : index + 1;
    setDragOverIdx(null);
    setDragOverCol(null);
    const drag = dragItemRef.current;
    if (!drag) return;
    if (drag.type === 'palette' && drag.blockType) {
      const newBlock = createEmailBlock(drag.blockType);
      const newBlocks = [...blocks];
      newBlocks.splice(dropIdx, 0, newBlock);
      updateBlocks(newBlocks);
      setSelectedId(newBlock.id);
      setSelectedChild(null);
    } else if (drag.type === 'canvas' && drag.index !== undefined) {
      if (drag.index === index) {
        dragItemRef.current = null;
        return;
      }
      const newBlocks = [...blocks];
      const [moved] = newBlocks.splice(drag.index, 1);
      const adjustedIdx = drag.index < dropIdx ? dropIdx - 1 : dropIdx;
      newBlocks.splice(adjustedIdx, 0, moved);
      updateBlocks(newBlocks);
    }
    dragItemRef.current = null;
  };
  const handleDrop = (dropIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverIdx(null);
    setDragOverCol(null);
    const drag = dragItemRef.current;
    if (!drag) return;
    if (drag.type === 'palette' && drag.blockType) {
      const newBlock = createEmailBlock(drag.blockType);
      const newBlocks = [...blocks];
      newBlocks.splice(dropIndex, 0, newBlock);
      updateBlocks(newBlocks);
      setSelectedId(newBlock.id);
      setSelectedChild(null);
    } else if (drag.type === 'canvas' && drag.index !== undefined) {
      const newBlocks = [...blocks];
      const [moved] = newBlocks.splice(drag.index, 1);
      newBlocks.splice(drag.index < dropIndex ? dropIndex - 1 : dropIndex, 0, moved);
      updateBlocks(newBlocks);
    }
    dragItemRef.current = null;
  };
  const handleCanvasDropEnd = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverIdx(null);
    setDragOverCol(null);
    const drag = dragItemRef.current;
    if (drag?.type === 'palette' && drag.blockType) {
      const newBlock = createEmailBlock(drag.blockType);
      updateBlocks([...blocks, newBlock]);
      setSelectedId(newBlock.id);
      setSelectedChild(null);
      if (newBlock.type === 'columns') {
        setColumnConfigModal({
          isOpen: true,
          blockId: newBlock.id,
          currentRatio: newBlock.columnRatio || '50-50'
        });
      }
    }
    dragItemRef.current = null;
  };
  // Column drop handlers
  const handleColumnDragOver = (parentId: string, colIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const drag = dragItemRef.current;
    if (!drag) return;
    // Only allow palette drops of allowed types into columns
    if (drag.type === 'palette' && drag.blockType && COLUMN_ALLOWED_TYPES.includes(drag.blockType)) {
      const parent = blocks.find(b => b.id === parentId);
      const col = parent?.columns?.[colIndex] || [];
      setDragOverCol({
        parentId,
        colIndex,
        position: col.length
      });
      setDragOverIdx(null);
      e.dataTransfer.dropEffect = 'copy';
    }
  };
  const handleColumnDrop = (parentId: string, colIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCol(null);
    setDragOverIdx(null);
    const drag = dragItemRef.current;
    if (!drag) return;
    if (drag.type === 'palette' && drag.blockType && COLUMN_ALLOWED_TYPES.includes(drag.blockType)) {
      const newBlock = createEmailBlock(drag.blockType);
      const newBlocks = blocks.map(b => {
        if (b.id !== parentId) return b;
        const cols = (b.columns || []).map((col, i) => i === colIndex ? [...col, newBlock] : col);
        return {
          ...b,
          columns: cols
        };
      });
      updateBlocks(newBlocks);
      setSelectedChild({
        parentId,
        colIndex,
        childId: newBlock.id
      });
      setSelectedId(null);
    }
    dragItemRef.current = null;
  };
  const deleteBlock = (id: string) => {
    updateBlocks(blocks.filter(b => b.id !== id));
    if (selectedId === id) setSelectedId(null);
  };
  const deleteChildBlock = (parentId: string, colIndex: number, childId: string) => {
    const newBlocks = blocks.map(b => {
      if (b.id !== parentId) return b;
      const cols = (b.columns || []).map((col, i) => i === colIndex ? col.filter(cb => cb.id !== childId) : col);
      return {
        ...b,
        columns: cols
      };
    });
    updateBlocks(newBlocks);
    if (selectedChild?.childId === childId) setSelectedChild(null);
  };
  const duplicateBlock = (id: string) => {
    const idx = blocks.findIndex(b => b.id === id);
    if (idx === -1) return;
    const original = blocks[idx];
    const clone: EmailBlock = {
      ...original,
      id: generateId(),
      columns: original.columns ? original.columns.map(col => col.map(cb => ({
        ...cb,
        id: generateId()
      }))) : undefined
    };
    const newBlocks = [...blocks];
    newBlocks.splice(idx + 1, 0, clone);
    updateBlocks(newBlocks);
    setSelectedId(clone.id);
    setSelectedChild(null);
  };
  const updateBlock = (id: string, updates: Partial<EmailBlock>) => {
    const newBlocks = blocks.map(b => b.id === id ? {
      ...b,
      ...updates
    } : b);
    setBlocks(newBlocks);
    onSchemaChange(newBlocks, styles);
    onHtmlChange(generateEmailHtml(newBlocks, styles));
  };
  const updateChildBlock = (parentId: string, colIndex: number, childId: string, updates: Partial<EmailBlock>) => {
    const newBlocks = blocks.map(b => {
      if (b.id !== parentId) return b;
      const cols = (b.columns || []).map((col, i) => i === colIndex ? col.map(cb => cb.id === childId ? {
        ...cb,
        ...updates
      } : cb) : col);
      return {
        ...b,
        columns: cols
      };
    });
    setBlocks(newBlocks);
    onSchemaChange(newBlocks, styles);
    onHtmlChange(generateEmailHtml(newBlocks, styles));
  };
  const commitToHistory = () => pushHistory(blocks);
  const categories = [...new Set(EMAIL_PALETTE.map(p => p.category))];
  const toggleCat = (cat: string) => {
    setCollapsedCats(prev => {
      const n = new Set(prev);
      n.has(cat) ? n.delete(cat) : n.add(cat);
      return n;
    });
  };
  // AI Beautify handler
  const handleBeautify = async (styleDirection?: string) => {
    if (blocks.length === 0) return;
    setIsBeautifying(true);
    try {
      const html = generateEmailHtml(blocks, styles);
      // Save current HTML so undo can revert
      setPreBeautifyHtml(html);
      const prompt = `Here is my email HTML:\n\`\`\`html\n${html}\n\`\`\`\n\n${styleDirection ? `User instruction: ${styleDirection}` : 'Make subtle improvements to polish the email. Keep the overall look intact.'}`;
      const result = await callGPTAPI(EMAIL_BEAUTIFY_INSTRUCTIONS, prompt);
      const newHtml = parseHtmlFromResponse(result);
      if (newHtml) onHtmlChange(newHtml);
    } catch (err) {
      console.error('Email beautify failed:', err);
      setPreBeautifyHtml(null); // Clear on failure
    } finally {
      setIsBeautifying(false);
      setShowBeautifyInput(false);
      setBeautifyPrompt('');
    }
  };
  // Handle property changes — route to correct updater based on selection
  const handlePropertyChange = (updates: Partial<EmailBlock>) => {
    if (selectedChild) {
      updateChildBlock(selectedChild.parentId, selectedChild.colIndex, selectedChild.childId, updates);
    } else if (selectedId) {
      updateBlock(selectedId, updates);
    }
  };
  const handlePropertyDelete = () => {
    if (selectedChild) {
      deleteChildBlock(selectedChild.parentId, selectedChild.colIndex, selectedChild.childId);
    } else if (selectedId) {
      deleteBlock(selectedId);
    }
  };
  const insertLayout = (layoutType: string) => {
    const newBlocks = [...blocks];
    switch (layoutType) {
      case 'image-text':
        newBlocks.push(createEmailBlock('image'), createEmailBlock('text'));
        break;
      case 'text-image-side':
        const cols = createEmailBlock('columns');
        cols.columns = [[createEmailBlock('text')], [createEmailBlock('image')]];
        newBlocks.push(cols);
        break;
      case 'image-text-side':
        const cols2 = createEmailBlock('columns');
        cols2.columns = [[createEmailBlock('image')], [createEmailBlock('text')]];
        newBlocks.push(cols2);
        break;
      case 'three-col':
        const cols3 = createEmailBlock('columns');
        cols3.columnRatio = '33-33-33';
        cols3.columns = [[createEmailBlock('image'), createEmailBlock('text')], [createEmailBlock('image'), createEmailBlock('text')], [createEmailBlock('image'), createEmailBlock('text')]];
        newBlocks.push(cols3);
        break;
      case 'hero':
        newBlocks.push(createEmailBlock('image'), createEmailBlock('heading'), createEmailBlock('text'), createEmailBlock('button'));
        break;
    }
    updateBlocks(newBlocks);
  };
  return <div className="flex h-[calc(100vh-220px)] min-h-[500px] border border-gray-200 rounded-lg overflow-hidden bg-white" data-id="element-138">
      {/* Left — Sidebar */}
      <div className="w-64 flex-shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col" data-id="element-139">
        <div className="flex border-b border-gray-200 bg-white" data-id="element-140">
          <button onClick={() => setSidebarTab('elements')} className={`flex-1 py-2.5 text-xs font-medium text-center border-b-2 transition-colors ${sidebarTab === 'elements' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-141">
            Elements
          </button>
          <button onClick={() => setSidebarTab('layouts')} className={`flex-1 py-2.5 text-xs font-medium text-center border-b-2 transition-colors ${sidebarTab === 'layouts' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-142">
            Layouts
          </button>
          <button onClick={() => setSidebarTab('design')} className={`flex-1 py-2.5 text-xs font-medium text-center border-b-2 transition-colors ${sidebarTab === 'design' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-143">
            Design
          </button>
        </div>

        <div className="flex-1 overflow-y-auto" data-id="element-144">
          {sidebarTab === 'elements' && <>
              {categories.map(cat => <div key={cat} data-id="element-145">
                  <button onClick={() => toggleCat(cat)} className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:bg-gray-100" data-id="element-146">
                    {cat}
                    <ChevronRightIcon className={`h-3 w-3 transition-transform ${collapsedCats.has(cat) ? '' : 'rotate-90'}`} data-id="element-147" />
                  </button>
                  {!collapsedCats.has(cat) && <div className="px-2 pb-2 grid grid-cols-2 gap-1" data-id="element-148">
                      {EMAIL_PALETTE.filter(p => p.category === cat).map(item => {
                const Icon = ICONS[item.icon] || TypeIcon;
                return <div key={item.type} draggable onDragStart={handlePaletteDragStart(item.type as EmailBlockType)} className="flex flex-col items-center justify-center gap-1.5 p-2 rounded-md bg-white border border-gray-200 text-xs text-gray-600 cursor-grab hover:border-blue-300 hover:shadow-sm active:cursor-grabbing select-none text-center" data-id="element-149">
                              <Icon className="h-5 w-5 text-gray-400 flex-shrink-0" data-id="element-150" />
                              <span className="truncate w-full" data-id="element-151">
                                {item.label}
                              </span>
                            </div>;
              })}
                    </div>}
                </div>)}

              {mergeVariables.length > 0 && <div className="border-t border-gray-200 mt-2 pt-2 px-3 pb-3" data-id="element-152">
                  <div className="flex items-center gap-1 mb-2" data-id="element-153">
                    <TagIcon className="h-3 w-3 text-gray-400" data-id="element-154" />
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-155">
                      Merge Tags
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1" data-id="element-156">
                    {mergeVariables.map(v => <button key={v} onClick={() => navigator.clipboard?.writeText(`{{${v}}}`)} className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100" title={`Copy {{${v}}}`} data-id="element-157">
                        {`{{${v}}}`}
                      </button>)}
                  </div>
                </div>}
            </>}

          {sidebarTab === 'layouts' && <div className="p-3 space-y-3" data-id="element-158">
              <p className="text-xs text-gray-500 mb-2" data-id="element-159">
                Click to insert layout at the bottom
              </p>

              <button onClick={() => insertLayout('image-text')} className="w-full flex flex-col gap-1 p-2 bg-white border border-gray-200 rounded hover:border-blue-400" data-id="element-160">
                <div className="h-12 bg-gray-200 rounded flex items-center justify-center" data-id="element-161">
                  <ImageIcon className="h-4 w-4 text-gray-400" data-id="element-162" />
                </div>
                <div className="h-2 bg-gray-200 rounded w-3/4" data-id="element-163"></div>
                <div className="h-2 bg-gray-200 rounded w-full" data-id="element-164"></div>
                <div className="h-2 bg-gray-200 rounded w-5/6" data-id="element-165"></div>
              </button>

              <button onClick={() => insertLayout('text-image-side')} className="w-full flex gap-2 p-2 bg-white border border-gray-200 rounded hover:border-blue-400" data-id="element-166">
                <div className="flex-1 flex flex-col gap-1 justify-center" data-id="element-167">
                  <div className="h-2 bg-gray-200 rounded w-full" data-id="element-168"></div>
                  <div className="h-2 bg-gray-200 rounded w-full" data-id="element-169"></div>
                  <div className="h-2 bg-gray-200 rounded w-3/4" data-id="element-170"></div>
                </div>
                <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center" data-id="element-171">
                  <ImageIcon className="h-4 w-4 text-gray-400" data-id="element-172" />
                </div>
              </button>

              <button onClick={() => insertLayout('image-text-side')} className="w-full flex gap-2 p-2 bg-white border border-gray-200 rounded hover:border-blue-400" data-id="element-173">
                <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center" data-id="element-174">
                  <ImageIcon className="h-4 w-4 text-gray-400" data-id="element-175" />
                </div>
                <div className="flex-1 flex flex-col gap-1 justify-center" data-id="element-176">
                  <div className="h-2 bg-gray-200 rounded w-full" data-id="element-177"></div>
                  <div className="h-2 bg-gray-200 rounded w-full" data-id="element-178"></div>
                  <div className="h-2 bg-gray-200 rounded w-3/4" data-id="element-179"></div>
                </div>
              </button>

              <button onClick={() => insertLayout('three-col')} className="w-full flex gap-2 p-2 bg-white border border-gray-200 rounded hover:border-blue-400" data-id="element-180">
                {[1, 2, 3].map(i => <div key={i} className="flex-1 flex flex-col gap-1" data-id="element-181">
                    <div className="h-8 bg-gray-200 rounded flex items-center justify-center" data-id="element-182">
                      <ImageIcon className="h-3 w-3 text-gray-400" data-id="element-183" />
                    </div>
                    <div className="h-1.5 bg-gray-200 rounded w-full" data-id="element-184"></div>
                    <div className="h-1.5 bg-gray-200 rounded w-4/5" data-id="element-185"></div>
                  </div>)}
              </button>

              <button onClick={() => insertLayout('hero')} className="w-full flex flex-col gap-1.5 p-2 bg-white border border-gray-200 rounded hover:border-blue-400 items-center" data-id="element-186">
                <div className="h-16 w-full bg-gray-200 rounded flex items-center justify-center" data-id="element-187">
                  <ImageIcon className="h-5 w-5 text-gray-400" data-id="element-188" />
                </div>
                <div className="h-3 bg-gray-300 rounded w-1/2 mt-1" data-id="element-189"></div>
                <div className="h-1.5 bg-gray-200 rounded w-3/4" data-id="element-190"></div>
                <div className="h-1.5 bg-gray-200 rounded w-2/3" data-id="element-191"></div>
                <div className="h-4 bg-blue-400 rounded w-1/3 mt-1" data-id="element-192"></div>
              </button>
            </div>}

          {sidebarTab === 'design' && <EmailStyleProperties styles={styles} onChange={setStyles} data-id="element-193" />}
        </div>
      </div>

      {/* Center — Canvas */}
      <div className="flex-1 overflow-y-auto bg-gray-100 p-6" onClick={() => {
      setSelectedId(null);
      setSelectedChild(null);
    }} data-id="element-194">
        <div className="flex items-center justify-between mb-4" data-id="element-195">
          <div className="flex items-center gap-1" data-id="element-196">
            <button onClick={undo} disabled={historyIdx <= 0} className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30" title="Undo" data-id="element-197">
              <UndoIcon className="h-4 w-4 text-gray-500" data-id="element-198" />
            </button>
            <button onClick={redo} disabled={historyIdx >= history.length - 1} className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30" title="Redo" data-id="element-199">
              <RedoIcon className="h-4 w-4 text-gray-500" data-id="element-200" />
            </button>
          </div>

          <div className="flex items-center gap-2" data-id="element-201">
            {showBeautifyInput && <div className="flex items-center gap-1.5 bg-white border border-purple-200 rounded-lg px-2 py-1 shadow-sm" data-id="element-202">
                <input type="text" value={beautifyPrompt} onChange={e => setBeautifyPrompt(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter') handleBeautify(beautifyPrompt);
              if (e.key === 'Escape') {
                setShowBeautifyInput(false);
                setBeautifyPrompt('');
              }
            }} placeholder="e.g. warm colors, minimal, corporate..." className="text-xs border-none outline-none bg-transparent w-48 placeholder:text-gray-400" autoFocus data-id="element-203" />
                <button onClick={() => handleBeautify(beautifyPrompt)} disabled={isBeautifying} className="p-1 text-purple-600 hover:text-purple-800 disabled:opacity-50" data-id="element-204">
                  <SendIcon className="h-3.5 w-3.5" data-id="element-205" />
                </button>
                <button onClick={() => {
              setShowBeautifyInput(false);
              setBeautifyPrompt('');
            }} className="p-1 text-gray-400 hover:text-gray-600" data-id="element-206">
                  <XIcon className="h-3.5 w-3.5" data-id="element-207" />
                </button>
              </div>}
            <button onClick={() => {
            if (showBeautifyInput) handleBeautify(beautifyPrompt);else if (blocks.length > 0) setShowBeautifyInput(true);
          }} disabled={isBeautifying || blocks.length === 0} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 disabled:opacity-50 transition-colors" title="Use AI to beautify your email" data-id="element-208">
              {isBeautifying ? <Loader2Icon className="h-3.5 w-3.5 animate-spin" data-id="element-209" /> : <WandSparklesIcon className="h-3.5 w-3.5" data-id="element-210" />}
              {isBeautifying ? 'Beautifying...' : '✨ AI Beautify'}
            </button>
            <span className="text-xs text-gray-400" data-id="element-211">
              {blocks.length} block{blocks.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        <div className="mx-auto rounded-lg border border-gray-200 shadow-sm overflow-hidden" style={{
        maxWidth: styles.contentWidth,
        backgroundColor: styles.innerColor,
        border: styles.outerBorderSize > 0 ? `${styles.outerBorderSize}px solid ${styles.outerBorderColor}` : 'none'
      }} dir={styles.rtlEnabled ? 'rtl' : 'ltr'} data-id="element-212">
          <div style={{
          backgroundColor: styles.innerColor
        }} data-id="element-213">
            {blocks.length === 0 && <div onDragOver={e => {
            e.preventDefault();
            setDragOverIdx(0);
          }} onDragLeave={() => setDragOverIdx(null)} onDrop={handleCanvasDropEnd} className={`border-2 border-dashed rounded-lg p-16 text-center transition-colors m-4 ${dragOverIdx === 0 ? 'border-blue-400 bg-blue-50' : 'border-gray-300'}`} data-id="element-214">
                <p className="text-sm text-gray-400" data-id="element-215">Drag email blocks here</p>
              </div>}

            {blocks.map((block, idx) => <Fragment key={block.id} data-id="element-216">
                <div onDragOver={handleDragOver(idx)} onDragLeave={() => {
              setDragOverIdx(null);
              setDragOverCol(null);
            }} onDrop={handleDrop(idx)} className={`transition-all ${dragOverIdx === idx ? 'h-2 bg-blue-400' : 'h-0.5'}`} data-id="element-217" />
                <div draggable onDragStart={handleCanvasDragStart(idx)} onDragOver={handleBlockDragOver(idx)} onDrop={handleBlockDrop(idx)} onClick={e => {
              e.stopPropagation();
              setSelectedId(block.id);
              setSelectedChild(null);
            }} className={`group relative border-2 transition-colors ${selectedId === block.id && !selectedChild ? 'border-blue-500' : 'border-transparent hover:border-gray-300'}`} data-id="element-218">
                  <div className="absolute right-1 top-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-10" data-id="element-219">
                    {block.type === 'columns' && <button onClick={e => {
                  e.stopPropagation();
                  setColumnConfigModal({
                    isOpen: true,
                    blockId: block.id,
                    currentRatio: block.columnRatio || '50-50'
                  });
                }} className="p-1 bg-white border border-gray-200 rounded shadow-sm hover:bg-gray-50" title="Configure Columns" data-id="element-220">
                        <SettingsIcon className="h-3 w-3 text-gray-500" data-id="element-221" />
                      </button>}
                    <button onClick={e => {
                  e.stopPropagation();
                  duplicateBlock(block.id);
                }} className="p-1 bg-white border border-gray-200 rounded shadow-sm hover:bg-gray-50" data-id="element-222">
                      <CopyIcon className="h-3 w-3 text-gray-500" data-id="element-223" />
                    </button>
                    <button onClick={e => {
                  e.stopPropagation();
                  deleteBlock(block.id);
                }} className="p-1 bg-white border border-gray-200 rounded shadow-sm hover:bg-red-50" data-id="element-224">
                      <Trash2Icon className="h-3 w-3 text-red-500" data-id="element-225" />
                    </button>
                  </div>
                  <div className="absolute left-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-226">
                    <GripVerticalIcon className="h-4 w-4 text-gray-400 cursor-grab" data-id="element-227" />
                  </div>

                  {/* Special rendering for columns with drop zones */}
                  {block.type === 'columns' ? <EmailColumnsCanvas block={block} styles={styles} selectedChild={selectedChild} dragOverCol={dragOverCol} onColumnDragOver={handleColumnDragOver} onColumnDrop={handleColumnDrop} onSelectChild={(colIndex, childId) => {
                setSelectedChild({
                  parentId: block.id,
                  colIndex,
                  childId
                });
                setSelectedId(null);
              }} onDeleteChild={(colIndex, childId) => deleteChildBlock(block.id, colIndex, childId)} data-id="element-228" /> : <EmailCanvasBlock block={block} styles={styles} data-id="element-229" />}
                </div>
              </Fragment>)}

            {blocks.length > 0 && <div onDragOver={e => {
            e.preventDefault();
            setDragOverIdx(blocks.length);
          }} onDragLeave={() => setDragOverIdx(null)} onDrop={handleDrop(blocks.length)} className={`transition-all ${dragOverIdx === blocks.length ? 'h-2 bg-blue-400' : 'h-4'}`} data-id="element-230" />}
          </div>
        </div>
      </div>

      {/* Right — Properties */}
      <div className="w-72 flex-shrink-0 border-l border-gray-200 bg-white overflow-y-auto" data-id="element-231">
        {selectedBlock ? <EmailBlockProperties block={selectedBlock} isChild={!!selectedChild} onChange={handlePropertyChange} onBlur={commitToHistory} onDelete={handlePropertyDelete} data-id="element-232" /> : <div className="p-6 text-center text-gray-400 text-sm mt-20" data-id="element-233">
            <MousePointerIcon className="h-8 w-8 mx-auto mb-3 opacity-20" data-id="element-234" />
            Select a block to edit its properties
          </div>}
      </div>

      {/* Column Configuration Modal */}
      {columnConfigModal && columnConfigModal.isOpen && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-235">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-236">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setColumnConfigModal(null)} data-id="element-237" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6" data-id="element-238">
              <div className="flex items-center justify-between mb-4" data-id="element-239">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-240">
                  Configure Columns
                </h3>
                <button onClick={() => setColumnConfigModal(null)} className="text-gray-400 hover:text-gray-500" data-id="element-241">
                  <XIcon className="h-5 w-5" data-id="element-242" />
                </button>
              </div>
              <div className="space-y-4" data-id="element-243">
                <div data-id="element-244">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-245">
                    Column Layout
                  </label>
                  <div className="grid grid-cols-2 gap-2" data-id="element-246">
                    {[{
                  value: '50-50',
                  label: '50 / 50',
                  cols: 2
                }, {
                  value: '33-67',
                  label: '33 / 67',
                  cols: 2
                }, {
                  value: '67-33',
                  label: '67 / 33',
                  cols: 2
                }, {
                  value: '33-33-33',
                  label: '33 / 33 / 33',
                  cols: 3
                }].map(option => <button key={option.value} onClick={() => {
                  const block = blocks.find(b => b.id === columnConfigModal.blockId);
                  if (block) {
                    const cols = [...(block.columns || [[], []])];
                    while (cols.length < option.cols) cols.push([]);
                    updateBlock(block.id, {
                      columnRatio: option.value as any,
                      columns: cols.slice(0, option.cols)
                    });
                  }
                  setColumnConfigModal(null);
                }} className={`py-2 px-3 border rounded-md text-sm font-medium transition-colors ${columnConfigModal.currentRatio === option.value ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-247">
                        {option.label}
                      </button>)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}
// ── Columns Canvas with Drop Zones ──
function EmailColumnsCanvas({
  block,
  styles,
  selectedChild,
  dragOverCol,
  onColumnDragOver,
  onColumnDrop,
  onSelectChild,
  onDeleteChild
}: {
  block: EmailBlock;
  styles: EmailGlobalStyles;
  selectedChild: {
    parentId: string;
    colIndex: number;
    childId: string;
  } | null;
  dragOverCol: {
    parentId: string;
    colIndex: number;
    position: number;
  } | null;
  onColumnDragOver: (parentId: string, colIndex: number) => (e: React.DragEvent) => void;
  onColumnDrop: (parentId: string, colIndex: number) => (e: React.DragEvent) => void;
  onSelectChild: (colIndex: number, childId: string) => void;
  onDeleteChild: (colIndex: number, childId: string) => void;
}) {
  const pad = {
    paddingTop: block.paddingTop ?? 10,
    paddingBottom: block.paddingBottom ?? 10,
    paddingLeft: block.paddingLeft ?? 20,
    paddingRight: block.paddingRight ?? 20
  };
  const bg = block.backgroundColor || undefined;
  const cols = block.columns || [[], []];
  const isOver = (colIdx: number) => dragOverCol?.parentId === block.id && dragOverCol?.colIndex === colIdx;
  return <div style={{
    ...pad,
    backgroundColor: bg
  }} data-id="element-248">
      <div style={{
      display: 'flex',
      gap: 0
    }} data-id="element-249">
        {cols.map((col, colIdx) => <div key={colIdx} onDragOver={onColumnDragOver(block.id, colIdx)} onDragLeave={e => e.stopPropagation()} onDrop={onColumnDrop(block.id, colIdx)} onClick={e => e.stopPropagation()} style={{
        flex: 1,
        border: isOver(colIdx) ? '2px dashed #3b82f6' : '1px dashed #d1d5db',
        backgroundColor: isOver(colIdx) ? '#eff6ff' : undefined,
        minHeight: 60,
        padding: 4,
        transition: 'border-color 0.15s, background-color 0.15s'
      }} data-id="element-250">
            {col.length === 0 ? <p style={{
          textAlign: 'center',
          color: '#9ca3af',
          fontSize: 12,
          padding: '16px 8px'
        }} data-id="element-251">
                {isOver(colIdx) ? '⬇ Drop here' : `Column ${colIdx + 1} — drag blocks here`}
              </p> : col.map(cb => <div key={cb.id} onClick={e => {
          e.stopPropagation();
          onSelectChild(colIdx, cb.id);
        }} className="relative group/child" style={{
          border: selectedChild?.parentId === block.id && selectedChild?.childId === cb.id ? '2px solid #3b82f6' : '1px solid transparent',
          borderRadius: 4,
          marginBottom: 2,
          cursor: 'pointer'
        }} data-id="element-252">
                  <div className="absolute -right-0.5 -top-0.5 opacity-0 group-hover/child:opacity-100 transition-opacity z-10" data-id="element-253">
                    <button onClick={e => {
              e.stopPropagation();
              onDeleteChild(colIdx, cb.id);
            }} className="p-0.5 bg-white border border-gray-200 rounded shadow-sm hover:bg-red-50" title="Remove from column" data-id="element-254">
                      <XIcon className="h-2.5 w-2.5 text-red-500" data-id="element-255" />
                    </button>
                  </div>
                  <EmailCanvasBlock block={cb} styles={styles} data-id="element-256" />
                </div>)}
          </div>)}
      </div>
    </div>;
}
// ── Canvas Preview ──
function EmailCanvasBlock({
  block,
  styles
}: {
  block: EmailBlock;
  styles: EmailGlobalStyles;
}) {
  const pad = {
    paddingTop: block.paddingTop ?? 16,
    paddingBottom: block.paddingBottom ?? 16,
    paddingLeft: block.paddingLeft ?? 16,
    paddingRight: block.paddingRight ?? 16
  };
  const bg = block.backgroundColor || 'transparent';
  const getImageStyle = (isImageText: boolean): React.CSSProperties => {
    const style: React.CSSProperties = {
      display: 'block'
    };
    const widthProp = isImageText ? 'imageTextImageWidth' : 'imageWidth';
    const sizing = block.imageSizing || 'maxWidth';
    if (sizing === 'natural') {
      style.maxWidth = 'none';
    } else if (sizing === 'custom') {
      if (block.imageHeight) style.height = `${block.imageHeight}px`;else style.height = 'auto';
      if (block.imageWidth) style.width = `${block.imageWidth}px`;
      if (block.imageMaxWidth) style.maxWidth = `${block.imageMaxWidth}%`;else style.maxWidth = '100%';
    } else {
      const w = (block as any)[widthProp] || block.imageMaxWidth || (isImageText ? 100 : 100);
      style.width = `${w}%`;
      style.maxWidth = '100%';
      style.height = 'auto';
    }
    if (block.imageMaxHeight) style.maxHeight = `${block.imageMaxHeight}px`;
    if (block.imageObjectFit) style.objectFit = block.imageObjectFit as any;
    if (block.imageBorderRadius) style.borderRadius = `${block.imageBorderRadius}px`;
    if (block.imageOpacity != null && block.imageOpacity < 100) style.opacity = block.imageOpacity / 100;
    if (block.imageShadow === 'sm') style.boxShadow = '0 1px 2px 0 rgba(0,0,0,0.05)';else if (block.imageShadow === 'md') style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.1)';else if (block.imageShadow === 'lg') style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1)';
    const align = block.imageAlign || block.align || 'center';
    if (align === 'center') style.margin = '0 auto';else if (align === 'right') style.marginLeft = 'auto';else if (align === 'left') style.marginRight = 'auto';
    return style;
  };
  switch (block.type) {
    case 'text':
      return <div style={{
        ...pad,
        backgroundColor: bg,
        fontFamily: styles.fontFamily,
        fontSize: block.fontSize || 16,
        lineHeight: block.lineHeight || 1.6,
        color: block.color || styles.textColor,
        textAlign: block.align || 'left'
      }} data-id="element-257">
          {block.content || 'Add text...'}
        </div>;
    case 'heading':
      return <div style={{
        ...pad,
        backgroundColor: bg,
        fontFamily: styles.fontFamily,
        fontSize: block.fontSize || 28,
        fontWeight: block.fontWeight || 'bold',
        lineHeight: block.lineHeight || 1.3,
        color: block.color || '#111827',
        textAlign: block.align || 'center'
      }} data-id="element-258">
          {block.content || 'Heading'}
        </div>;
    case 'button':
      return <div style={{
        ...pad,
        backgroundColor: bg,
        textAlign: block.align || 'center'
      }} data-id="element-259">
          <div style={{
          display: block.buttonWidth === 'full' ? 'block' : 'inline-block',
          backgroundColor: block.buttonBgColor || '#3b82f6',
          color: block.buttonTextColor || '#ffffff',
          fontFamily: styles.fontFamily,
          fontSize: block.fontSize || 16,
          fontWeight: block.fontWeight || '600',
          padding: '12px 24px',
          borderRadius: block.buttonBorderRadius || 4,
          textAlign: 'center'
        }} data-id="element-260">
            {block.buttonText || 'Click Here'}
          </div>
        </div>;
    case 'image':
      return <div style={{
        ...pad,
        backgroundColor: bg,
        textAlign: block.imageAlign || block.align || 'center'
      }} data-id="element-261">
          {block.src ? <img src={block.src} alt={block.alt} style={getImageStyle(false)} data-id="element-262" /> : <div style={{
          background: '#f3f4f6',
          border: '2px dashed #d1d5db',
          padding: 40,
          textAlign: 'center',
          color: '#9ca3af',
          fontSize: 14
        }} data-id="element-263">
              Image — set URL in properties
            </div>}
        </div>;
    case 'video_thumbnail':
      return <div style={{
        ...pad,
        backgroundColor: bg,
        textAlign: block.align || 'center'
      }} data-id="element-264">
          <div style={{
          position: 'relative',
          display: 'inline-block'
        }} data-id="element-265">
            {block.src ? <img src={block.src} alt={block.alt} style={{
            maxWidth: '100%',
            borderRadius: 8
          }} data-id="element-266" /> : <div style={{
            background: '#1f2937',
            padding: '60px 80px',
            borderRadius: 8,
            color: '#fff',
            fontSize: 14
          }} data-id="element-267">
                Video Thumbnail
              </div>}
            <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            width: 50,
            height: 50,
            background: 'rgba(0,0,0,0.6)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} data-id="element-268">
              <PlayIcon className="h-5 w-5 text-white ml-0.5" data-id="element-269" />
            </div>
          </div>
        </div>;
    case 'divider':
      return <div style={{
        ...pad,
        backgroundColor: bg
      }} data-id="element-270">
          <hr style={{
          border: 'none',
          borderTop: `${block.dividerThickness || 1}px ${block.dividerStyle || 'solid'} ${block.dividerColor || '#e5e7eb'}`
        }} data-id="element-271" />
        </div>;
    case 'spacer':
      return <div style={{
        height: block.height || 20,
        backgroundColor: bg
      }} className="flex items-center justify-center text-xs text-gray-400" data-id="element-272">
          ↕ {block.height || 20}px
        </div>;
    case 'social':
      {
        const socialColors: Record<string, string> = {
          facebook: '#1877F2',
          twitter: '#000000',
          instagram: '#E1306C',
          linkedin: '#0A66C2',
          youtube: '#FF0000',
          tiktok: '#000000',
          pinterest: '#BD081C',
          whatsapp: '#25D366'
        };
        const socialSvgs: Record<string, string> = {
          facebook: '<path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>',
          twitter: '<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>',
          instagram: '<path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>',
          linkedin: '<path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>',
          youtube: '<path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>',
          tiktok: '<path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>',
          pinterest: '<path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001 12.017.001z"/>',
          whatsapp: '<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>'
        };
        return <div style={{
          ...pad,
          backgroundColor: bg,
          textAlign: block.align || 'center'
        }} data-id="element-273">
          <div style={{
            display: 'inline-flex',
            gap: 8
          }} data-id="element-274">
            {(block.socialLinks || []).map((link, i) => {
              const color = socialColors[link.platform.toLowerCase()] || '#6b7280';
              const svgPath = socialSvgs[link.platform.toLowerCase()] || '';
              const size = block.socialIconSize || 32;
              return <div key={i} style={{
                width: size,
                height: size,
                backgroundColor: color,
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }} title={link.platform} data-id="element-275">
                  <svg viewBox="0 0 24 24" fill="#ffffff" style={{
                  width: size * 0.55,
                  height: size * 0.55
                }} data-id="element-276">
                    <g dangerouslySetInnerHTML={{
                    __html: svgPath
                  }} data-id="element-277" />
                  </svg>
                </div>;
            })}
          </div>
        </div>;
      }
    case 'unsubscribe':
      return <div style={{
        ...pad,
        backgroundColor: bg,
        textAlign: block.align as any || 'center',
        fontSize: block.fontSize || 12,
        lineHeight: block.lineHeight || 1.5,
        color: block.color || '#9ca3af'
      }} dangerouslySetInnerHTML={{
        __html: block.content || '<a href="#" style="color:#6b7280;">Unsubscribe</a>'
      }} data-id="element-278" />;
    case 'image_text':
      const imgWidth = block.imageTextImageWidth || 50;
      const textWidth = 100 - imgWidth;
      return <div style={{
        ...pad,
        backgroundColor: bg,
        display: 'flex',
        flexDirection: block.imageTextLayout === 'image-right' ? 'row-reverse' : 'row',
        gap: 20
      }} data-id="element-279">
          <div style={{
          width: `${imgWidth}%`
        }} data-id="element-280">
            {block.imageTextImageSrc ? <img src={block.imageTextImageSrc} alt={block.imageTextImageAlt || ''} style={getImageStyle(true)} data-id="element-281" /> : <div style={{
            background: '#f3f4f6',
            border: '2px dashed #d1d5db',
            padding: 20,
            textAlign: 'center',
            color: '#9ca3af',
            fontSize: 14
          }} data-id="element-282">
                Image
              </div>}
          </div>
          <div style={{
          width: `${textWidth}%`
        }} data-id="element-283">
            <div style={{
            fontFamily: styles.fontFamily,
            fontSize: block.fontSize || 16,
            lineHeight: block.lineHeight || 1.6,
            color: block.color || styles.textColor,
            textAlign: block.align || 'left',
            whiteSpace: 'pre-wrap'
          }} data-id="element-284">
              {block.imageTextContent || 'Add text here...'}
            </div>
          </div>
        </div>;
    case 'footer':
      return <div style={{
        ...pad,
        backgroundColor: bg,
        fontFamily: styles.fontFamily,
        fontSize: block.fontSize || 12,
        lineHeight: block.lineHeight || 1.5,
        color: block.color || '#9ca3af',
        textAlign: block.align || 'center'
      }} data-id="element-285">
          {block.footerLinks && block.footerLinks.length > 0 && <div style={{
          marginBottom: 10
        }} data-id="element-286">
              {block.footerLinks.map((l, i) => <a key={i} href={l.url} style={{
            color: styles.linkColor,
            textDecoration: 'underline',
            margin: '0 10px'
          }} data-id="element-287">
                  {l.label}
                </a>)}
            </div>}
          <div style={{
          marginBottom: 5,
          fontWeight: 'bold'
        }} data-id="element-288">
            {block.footerCompanyName || 'Company Name'}
          </div>
          <div data-id="element-289">{block.footerAddress || 'Address'}</div>
        </div>;
    case 'table':
      return <div style={{
        ...pad,
        backgroundColor: bg
      }} data-id="element-290">
          <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontFamily: styles.fontFamily,
          fontSize: block.fontSize || 14,
          color: block.color || styles.textColor,
          textAlign: block.align || 'left'
        }} border={block.tableData ? 0 : 1} borderColor={block.tableData ? undefined : '#e5e7eb'} cellPadding={block.tableData ? 0 : 10} data-id="element-291">
            <tbody dangerouslySetInnerHTML={{
            __html: block.content || ''
          }} data-id="element-292" />
          </table>
        </div>;
    case 'boxed_text':
      return <div style={{
        ...pad,
        backgroundColor: bg
      }} data-id="element-293">
          <div style={{
          border: block.boxedTextBorderColor ? `1px solid ${block.boxedTextBorderColor}` : 'none',
          borderRadius: block.boxedTextBorderRadius || 0,
          backgroundColor: block.boxedTextBgColor || 'transparent',
          padding: 20,
          fontFamily: styles.fontFamily,
          fontSize: block.fontSize || 16,
          lineHeight: block.lineHeight || 1.6,
          color: block.color || styles.textColor,
          textAlign: block.align || 'left'
        }} data-id="element-294">
            {block.content || 'Boxed text content'}
          </div>
        </div>;
    case 'html_block':
      return <div style={{
        ...pad,
        backgroundColor: bg
      }} data-id="element-295">
          <div style={{
          padding: 10,
          background: '#f8fafc',
          border: '1px dashed #cbd5e1',
          color: '#64748b',
          fontSize: 12,
          fontFamily: 'monospace',
          overflow: 'hidden'
        }} data-id="element-296">
            HTML Block Preview
            <br data-id="element-297" />
            {block.htmlContent ? <span className="text-gray-400 truncate block" data-id="element-298">
                {block.htmlContent.substring(0, 50)}...
              </span> : 'Empty HTML'}
          </div>
        </div>;
    default:
      return <div style={pad} data-id="element-299">Unknown block</div>;
  }
}
// ── Properties Panels ──
function EmailBlockProperties({
  block,
  isChild,
  onChange,
  onBlur,
  onDelete
}: {
  block: EmailBlock;
  isChild: boolean;
  onChange: (u: Partial<EmailBlock>) => void;
  onBlur: () => void;
  onDelete: () => void;
}) {
  return <div className="p-4 space-y-4" data-id="element-300">
      <div className="flex items-center justify-between pb-3 border-b border-gray-200" data-id="element-301">
        <div data-id="element-302">
          <h3 className="text-sm font-semibold text-gray-900 capitalize" data-id="element-303">
            {block.type.replace('_', ' ')}
          </h3>
          {isChild && <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded font-medium" data-id="element-304">
              Inside column
            </span>}
        </div>
        <button onClick={onDelete} className="p-1 text-red-500 hover:bg-red-50 rounded" data-id="element-305">
          <Trash2Icon className="h-4 w-4" data-id="element-306" />
        </button>
      </div>

      {(block.type === 'text' || block.type === 'heading' || block.type === 'unsubscribe' || block.type === 'boxed_text') && <>
          <EProp label="Content" data-id="element-307">
            <textarea value={block.content || ''} onChange={e => onChange({
          content: e.target.value
        })} onBlur={onBlur} rows={4} className="prop-input text-sm" data-id="element-308" />
          </EProp>
          <EProp label="Font Size" data-id="element-309">
            <input type="number" value={block.fontSize || 16} onChange={e => onChange({
          fontSize: Number(e.target.value)
        })} onBlur={onBlur} min={10} max={72} className="prop-input" data-id="element-310" />
          </EProp>
          <EProp label="Color" data-id="element-311">
            <div className="flex gap-2" data-id="element-312">
              <input type="color" value={block.color || '#333333'} onChange={e => onChange({
            color: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-313" />
              <input type="text" value={block.color || '#333333'} onChange={e => onChange({
            color: e.target.value
          })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-314" />
            </div>
          </EProp>
          <EProp label="Align" data-id="element-315">
            <select value={block.align || 'left'} onChange={e => {
          onChange({
            align: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-316">
              <option value="left" data-id="element-317">Left</option>
              <option value="center" data-id="element-318">Center</option>
              <option value="right" data-id="element-319">Right</option>
            </select>
          </EProp>
          {block.type === 'heading' && <EProp label="Font Weight" data-id="element-320">
              <select value={block.fontWeight || 'bold'} onChange={e => {
          onChange({
            fontWeight: e.target.value
          });
          onBlur();
        }} className="prop-input" data-id="element-321">
                <option value="normal" data-id="element-322">Normal</option>
                <option value="500" data-id="element-323">Medium</option>
                <option value="600" data-id="element-324">Semibold</option>
                <option value="bold" data-id="element-325">Bold</option>
              </select>
            </EProp>}
          <EProp label="Line Height" data-id="element-326">
            <input type="number" value={block.lineHeight || 1.6} onChange={e => onChange({
          lineHeight: Number(e.target.value)
        })} onBlur={onBlur} min={1} max={3} step={0.1} className="prop-input" data-id="element-327" />
          </EProp>
        </>}

      {block.type === 'button' && <>
          <EProp label="Button Text" data-id="element-328">
            <input type="text" value={block.buttonText || ''} onChange={e => onChange({
          buttonText: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-329" />
          </EProp>
          <EProp label="Link URL" data-id="element-330">
            <input type="url" value={block.buttonUrl || ''} onChange={e => onChange({
          buttonUrl: e.target.value
        })} onBlur={onBlur} className="prop-input" placeholder="https://..." data-id="element-331" />
            <div className="mt-2 flex flex-wrap gap-2" data-id="element-332">
              <button onClick={() => {
            onChange({
              buttonUrl: '{{confirmation_link}}'
            });
            onBlur();
          }} className="flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-md border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors" title="Insert confirmation link" data-id="element-333">
                <TagIcon className="h-3 w-3" data-id="element-334" />
                <span data-id="element-335">&#123;&#123;confirmation_link&#125;&#125;</span>
              </button>
              <button onClick={() => {
            onChange({
              buttonUrl: '{{unsubscribe_link}}'
            });
            onBlur();
          }} className="flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-md border border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 transition-colors" title="Insert unsubscribe link" data-id="element-336">
                <TagIcon className="h-3 w-3" data-id="element-337" />
                <span data-id="element-338">&#123;&#123;unsubscribe_link&#125;&#125;</span>
              </button>
            </div>
            <div className="mt-2 rounded-lg bg-blue-50 border border-blue-100 p-2 text-[10px] text-blue-700 leading-relaxed" data-id="element-339">
              <strong data-id="element-340">Note:</strong> The actual confirmation URL is configured
              in the Form Configuration settings where this template is linked.
            </div>
          </EProp>
          <EProp label="Background Color" data-id="element-341">
            <div className="flex gap-2" data-id="element-342">
              <input type="color" value={block.buttonBgColor || '#3b82f6'} onChange={e => onChange({
            buttonBgColor: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-343" />
              <input type="text" value={block.buttonBgColor || '#3b82f6'} onChange={e => onChange({
            buttonBgColor: e.target.value
          })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-344" />
            </div>
          </EProp>
          <EProp label="Text Color" data-id="element-345">
            <div className="flex gap-2" data-id="element-346">
              <input type="color" value={block.buttonTextColor || '#ffffff'} onChange={e => onChange({
            buttonTextColor: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-347" />
              <input type="text" value={block.buttonTextColor || '#ffffff'} onChange={e => onChange({
            buttonTextColor: e.target.value
          })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-348" />
            </div>
          </EProp>
          <EProp label="Border Radius" data-id="element-349">
            <input type="number" value={block.buttonBorderRadius || 4} onChange={e => onChange({
          buttonBorderRadius: Number(e.target.value)
        })} onBlur={onBlur} min={0} max={50} className="prop-input" data-id="element-350" />
          </EProp>
          <EProp label="Width" data-id="element-351">
            <select value={block.buttonWidth || 'auto'} onChange={e => {
          onChange({
            buttonWidth: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-352">
              <option value="auto" data-id="element-353">Auto (fit text)</option>
              <option value="full" data-id="element-354">Full Width</option>
            </select>
          </EProp>
        </>}

      {block.type === 'image' && <EmailImageBlockProperties block={block} onChange={onChange} onBlur={onBlur} data-id="element-355" />}

      {block.type === 'video_thumbnail' && <>
          <EProp label="Video URL" data-id="element-356">
            <input type="url" value={block.videoUrl || ''} onChange={e => onChange({
          videoUrl: e.target.value
        })} onBlur={onBlur} className="prop-input" placeholder="YouTube or Vimeo URL" data-id="element-357" />
          </EProp>
          <EProp label="Thumbnail URL" data-id="element-358">
            <input type="url" value={block.src || ''} onChange={e => onChange({
          src: e.target.value
        })} onBlur={onBlur} className="prop-input" placeholder="Auto-detected from YouTube" data-id="element-359" />
          </EProp>
          <EProp label="Alt Text" data-id="element-360">
            <input type="text" value={block.alt || ''} onChange={e => onChange({
          alt: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-361" />
          </EProp>
        </>}

      {block.type === 'divider' && <>
          <EProp label="Color" data-id="element-362">
            <div className="flex gap-2" data-id="element-363">
              <input type="color" value={block.dividerColor || '#e5e7eb'} onChange={e => onChange({
            dividerColor: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-364" />
              <input type="text" value={block.dividerColor || '#e5e7eb'} onChange={e => onChange({
            dividerColor: e.target.value
          })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-365" />
            </div>
          </EProp>
          <EProp label="Thickness" data-id="element-366">
            <input type="number" value={block.dividerThickness || 1} onChange={e => onChange({
          dividerThickness: Number(e.target.value)
        })} onBlur={onBlur} min={1} max={10} className="prop-input" data-id="element-367" />
          </EProp>
          <EProp label="Style" data-id="element-368">
            <select value={block.dividerStyle || 'solid'} onChange={e => {
          onChange({
            dividerStyle: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-369">
              <option value="solid" data-id="element-370">Solid</option>
              <option value="dashed" data-id="element-371">Dashed</option>
              <option value="dotted" data-id="element-372">Dotted</option>
            </select>
          </EProp>
        </>}

      {block.type === 'spacer' && <EProp label="Height (px)" data-id="element-373">
          <input type="number" value={block.height || 20} onChange={e => onChange({
        height: Number(e.target.value)
      })} onBlur={onBlur} min={4} max={200} className="prop-input" data-id="element-374" />
        </EProp>}

      {block.type === 'columns' && <EProp label="Column Ratio" data-id="element-375">
          <select value={block.columnRatio || '50-50'} onChange={e => {
        const ratio = e.target.value as any;
        const colCount = ratio === '33-33-33' ? 3 : 2;
        const cols = [...(block.columns || [[], []])];
        while (cols.length < colCount) cols.push([]);
        onChange({
          columnRatio: ratio,
          columns: cols.slice(0, colCount)
        });
        onBlur();
      }} className="prop-input" data-id="element-376">
            <option value="50-50" data-id="element-377">50 / 50</option>
            <option value="33-67" data-id="element-378">33 / 67</option>
            <option value="67-33" data-id="element-379">67 / 33</option>
            <option value="33-33-33" data-id="element-380">33 / 33 / 33</option>
          </select>
        </EProp>}

      {block.type === 'image_text' && <EmailImageBlockProperties block={block} onChange={onChange} onBlur={onBlur} data-id="element-381" />}

      {block.type === 'footer' && <>
          <EProp label="Company Name" data-id="element-382">
            <input type="text" value={block.footerCompanyName || ''} onChange={e => onChange({
          footerCompanyName: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-383" />
          </EProp>
          <EProp label="Address" data-id="element-384">
            <textarea value={block.footerAddress || ''} onChange={e => onChange({
          footerAddress: e.target.value
        })} onBlur={onBlur} rows={2} className="prop-input text-sm" data-id="element-385" />
          </EProp>
          <div className="space-y-3" data-id="element-386">
            <div className="flex items-center justify-between" data-id="element-387">
              <span className="text-xs font-medium text-gray-700" data-id="element-388">Links</span>
              <button onClick={() => {
            const links = [...(block.footerLinks || [])];
            links.push({
              label: 'New Link',
              url: '#'
            });
            onChange({
              footerLinks: links
            });
            onBlur();
          }} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5" data-id="element-389">
                <PlusIcon className="h-3 w-3" data-id="element-390" /> Add
              </button>
            </div>
            {(block.footerLinks || []).map((link, i) => <div key={i} className="space-y-1 p-2 bg-gray-50 rounded-md border border-gray-100" data-id="element-391">
                <div className="flex items-center gap-1" data-id="element-392">
                  <input type="text" value={link.label} onChange={e => {
              const links = [...(block.footerLinks || [])];
              links[i] = {
                ...links[i],
                label: e.target.value
              };
              onChange({
                footerLinks: links
              });
            }} onBlur={onBlur} placeholder="Label" className="prop-input flex-1 text-xs" data-id="element-393" />
                  <button onClick={() => {
              const links = (block.footerLinks || []).filter((_, j) => j !== i);
              onChange({
                footerLinks: links
              });
              onBlur();
            }} className="p-1 text-gray-400 hover:text-red-500" data-id="element-394">
                    <XIcon className="h-3 w-3" data-id="element-395" />
                  </button>
                </div>
                <input type="url" value={link.url} onChange={e => {
            const links = [...(block.footerLinks || [])];
            links[i] = {
              ...links[i],
              url: e.target.value
            };
            onChange({
              footerLinks: links
            });
          }} onBlur={onBlur} placeholder="https://..." className="prop-input text-xs w-full" data-id="element-396" />
              </div>)}
          </div>
          <EProp label="Font Size" data-id="element-397">
            <input type="number" value={block.fontSize || 12} onChange={e => onChange({
          fontSize: Number(e.target.value)
        })} onBlur={onBlur} className="prop-input" data-id="element-398" />
          </EProp>
          <EProp label="Text Color" data-id="element-399">
            <div className="flex gap-2" data-id="element-400">
              <input type="color" value={block.color || '#9ca3af'} onChange={e => onChange({
            color: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-401" />
              <input type="text" value={block.color || '#9ca3af'} onChange={e => onChange({
            color: e.target.value
          })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-402" />
            </div>
          </EProp>
        </>}

      {block.type === 'table' && <EmailTableBlockProperties block={block} onChange={onChange} onBlur={onBlur} data-id="element-403" />}

      {block.type === 'boxed_text' && <>
          <EProp label="Box Background" data-id="element-404">
            <div className="flex gap-2" data-id="element-405">
              <input type="color" value={block.boxedTextBgColor || '#f9fafb'} onChange={e => onChange({
            boxedTextBgColor: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-406" />
              <input type="text" value={block.boxedTextBgColor || '#f9fafb'} onChange={e => onChange({
            boxedTextBgColor: e.target.value
          })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-407" />
            </div>
          </EProp>
          <EProp label="Border Color" data-id="element-408">
            <div className="flex gap-2" data-id="element-409">
              <input type="color" value={block.boxedTextBorderColor || '#e5e7eb'} onChange={e => onChange({
            boxedTextBorderColor: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-410" />
              <input type="text" value={block.boxedTextBorderColor || '#e5e7eb'} onChange={e => onChange({
            boxedTextBorderColor: e.target.value
          })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-411" />
            </div>
          </EProp>
          <EProp label="Border Radius" data-id="element-412">
            <input type="number" value={block.boxedTextBorderRadius || 0} onChange={e => onChange({
          boxedTextBorderRadius: Number(e.target.value)
        })} onBlur={onBlur} className="prop-input" data-id="element-413" />
          </EProp>
        </>}

      {block.type === 'html_block' && <EProp label="Custom HTML" data-id="element-414">
          <textarea value={block.htmlContent || ''} onChange={e => onChange({
        htmlContent: e.target.value
      })} onBlur={onBlur} rows={10} className="prop-input text-sm font-mono" placeholder="<div>...</div>" data-id="element-415" />
        </EProp>}

      {/* Social Links Editor */}
      {block.type === 'social' && <div className="space-y-3" data-id="element-416">
          <div className="flex items-center justify-between" data-id="element-417">
            <span className="text-xs font-medium text-gray-700" data-id="element-418">
              Social Links
            </span>
            <button onClick={() => {
          const links = [...(block.socialLinks || [])];
          // Find a platform not yet used
          const used = new Set(links.map(l => l.platform));
          const next = SOCIAL_PLATFORMS.find(p => !used.has(p)) || 'facebook';
          links.push({
            platform: next,
            url: '#'
          });
          onChange({
            socialLinks: links
          });
          onBlur();
        }} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5" data-id="element-419">
              <PlusIcon className="h-3 w-3" data-id="element-420" /> Add
            </button>
          </div>
          {(block.socialLinks || []).map((link, i) => <div key={i} className="space-y-1 p-2 bg-gray-50 rounded-md border border-gray-100" data-id="element-421">
              <div className="flex items-center gap-1" data-id="element-422">
                <select value={link.platform} onChange={e => {
            const links = [...(block.socialLinks || [])];
            links[i] = {
              ...links[i],
              platform: e.target.value
            };
            onChange({
              socialLinks: links
            });
          }} className="prop-input flex-1 text-xs capitalize" data-id="element-423">
                  {SOCIAL_PLATFORMS.map(p => <option key={p} value={p} data-id="element-424">
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </option>)}
                </select>
                <button onClick={() => {
            const links = (block.socialLinks || []).filter((_, j) => j !== i);
            onChange({
              socialLinks: links
            });
            onBlur();
          }} className="p-1 text-gray-400 hover:text-red-500" data-id="element-425">
                  <XIcon className="h-3 w-3" data-id="element-426" />
                </button>
              </div>
              <input type="url" value={link.url} onChange={e => {
          const links = [...(block.socialLinks || [])];
          links[i] = {
            ...links[i],
            url: e.target.value
          };
          onChange({
            socialLinks: links
          });
        }} onBlur={onBlur} placeholder="https://..." className="prop-input text-xs w-full" data-id="element-427" />
            </div>)}
          <EProp label="Icon Size" data-id="element-428">
            <input type="number" value={block.socialIconSize || 32} onChange={e => onChange({
          socialIconSize: Number(e.target.value)
        })} onBlur={onBlur} min={16} max={64} className="prop-input" data-id="element-429" />
          </EProp>
          <EProp label="Align" data-id="element-430">
            <select value={block.align || 'center'} onChange={e => {
          onChange({
            align: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-431">
              <option value="left" data-id="element-432">Left</option>
              <option value="center" data-id="element-433">Center</option>
              <option value="right" data-id="element-434">Right</option>
            </select>
          </EProp>
        </div>}

      {/* Padding — shared for all blocks */}
      <div className="pt-3 border-t border-gray-200" data-id="element-435">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-436">
          Padding
        </span>
        <div className="grid grid-cols-2 gap-2 mt-2" data-id="element-437">
          <EProp label="Top" data-id="element-438">
            <input type="number" value={block.paddingTop ?? 10} onChange={e => onChange({
            paddingTop: Number(e.target.value)
          })} onBlur={onBlur} min={0} className="prop-input" data-id="element-439" />
          </EProp>
          <EProp label="Bottom" data-id="element-440">
            <input type="number" value={block.paddingBottom ?? 10} onChange={e => onChange({
            paddingBottom: Number(e.target.value)
          })} onBlur={onBlur} min={0} className="prop-input" data-id="element-441" />
          </EProp>
          <EProp label="Left" data-id="element-442">
            <input type="number" value={block.paddingLeft ?? 20} onChange={e => onChange({
            paddingLeft: Number(e.target.value)
          })} onBlur={onBlur} min={0} className="prop-input" data-id="element-443" />
          </EProp>
          <EProp label="Right" data-id="element-444">
            <input type="number" value={block.paddingRight ?? 20} onChange={e => onChange({
            paddingRight: Number(e.target.value)
          })} onBlur={onBlur} min={0} className="prop-input" data-id="element-445" />
          </EProp>
        </div>
      </div>

      <EProp label="Background" data-id="element-446">
        <div className="flex gap-2" data-id="element-447">
          <input type="color" value={block.backgroundColor || '#ffffff'} onChange={e => onChange({
          backgroundColor: e.target.value
        })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-448" />
          <input type="text" value={block.backgroundColor || ''} onChange={e => onChange({
          backgroundColor: e.target.value
        })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" placeholder="transparent" data-id="element-449" />
        </div>
      </EProp>
    </div>;
}
function EmailStyleProperties({
  styles,
  onChange
}: {
  styles: EmailGlobalStyles;
  onChange: (s: EmailGlobalStyles) => void;
}) {
  return <div className="p-4 space-y-4" data-id="element-450">
      <div className="pb-3 border-b border-gray-200" data-id="element-451">
        <div className="flex items-center gap-2" data-id="element-452">
          <PaletteIcon className="h-4 w-4 text-gray-400" data-id="element-453" />
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-454">
            Design Settings
          </h3>
        </div>
      </div>

      <div className="space-y-4" data-id="element-455">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-456">
          Layout
        </h4>
        <EProp label="Template Width (px)" data-id="element-457">
          <input type="number" value={styles.contentWidth} onChange={e => onChange({
          ...styles,
          contentWidth: Number(e.target.value)
        })} min={400} max={800} className="prop-input" data-id="element-458" />
        </EProp>
        <EProp label="Enable RTL layout" data-id="element-459">
          <div className="flex items-center mt-1" data-id="element-460">
            <input type="checkbox" checked={styles.rtlEnabled || false} onChange={e => onChange({
            ...styles,
            rtlEnabled: e.target.checked
          })} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-461" />
            <span className="ml-2 text-sm text-gray-600" data-id="element-462">
              Right-to-left text
            </span>
          </div>
        </EProp>
      </div>

      <div className="space-y-4 pt-4 border-t border-gray-200" data-id="element-463">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-464">
          Colors
        </h4>
        <EProp label="Inner Color (Content)" data-id="element-465">
          <div className="flex gap-2" data-id="element-466">
            <input type="color" value={styles.innerColor || '#ffffff'} onChange={e => onChange({
            ...styles,
            innerColor: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-467" />
            <input type="text" value={styles.innerColor || '#ffffff'} onChange={e => onChange({
            ...styles,
            innerColor: e.target.value
          })} className="prop-input flex-1 font-mono text-xs" data-id="element-468" />
          </div>
        </EProp>
        <EProp label="Outer Color (Background)" data-id="element-469">
          <div className="flex gap-2" data-id="element-470">
            <input type="color" value={styles.backgroundColor} onChange={e => onChange({
            ...styles,
            backgroundColor: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-471" />
            <input type="text" value={styles.backgroundColor} onChange={e => onChange({
            ...styles,
            backgroundColor: e.target.value
          })} className="prop-input flex-1 font-mono text-xs" data-id="element-472" />
          </div>
        </EProp>
      </div>

      <div className="space-y-4 pt-4 border-t border-gray-200" data-id="element-473">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-474">
          Border
        </h4>
        <EProp label="Outer Border Size (px)" data-id="element-475">
          <input type="number" value={styles.outerBorderSize || 0} onChange={e => onChange({
          ...styles,
          outerBorderSize: Number(e.target.value)
        })} min={0} max={20} className="prop-input" data-id="element-476" />
        </EProp>
        <EProp label="Outer Border Color" data-id="element-477">
          <div className="flex gap-2" data-id="element-478">
            <input type="color" value={styles.outerBorderColor || '#000000'} onChange={e => onChange({
            ...styles,
            outerBorderColor: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-479" />
            <input type="text" value={styles.outerBorderColor || '#000000'} onChange={e => onChange({
            ...styles,
            outerBorderColor: e.target.value
          })} className="prop-input flex-1 font-mono text-xs" data-id="element-480" />
          </div>
        </EProp>
      </div>

      <div className="space-y-4 pt-4 border-t border-gray-200" data-id="element-481">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-482">
          Typography
        </h4>
        <EProp label="Font Family" data-id="element-483">
          <select value={styles.fontFamily} onChange={e => onChange({
          ...styles,
          fontFamily: e.target.value
        })} className="prop-input" data-id="element-484">
            <option value="Arial, Helvetica, sans-serif" data-id="element-485">Arial</option>
            <option value="Georgia, serif" data-id="element-486">Georgia</option>
            <option value="'Trebuchet MS', sans-serif" data-id="element-487">Trebuchet MS</option>
            <option value="Verdana, sans-serif" data-id="element-488">Verdana</option>
            <option value="'Times New Roman', serif" data-id="element-489">Times New Roman</option>
          </select>
        </EProp>
        <EProp label="Text Color" data-id="element-490">
          <div className="flex gap-2" data-id="element-491">
            <input type="color" value={styles.textColor} onChange={e => onChange({
            ...styles,
            textColor: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-492" />
            <input type="text" value={styles.textColor} onChange={e => onChange({
            ...styles,
            textColor: e.target.value
          })} className="prop-input flex-1 font-mono text-xs" data-id="element-493" />
          </div>
        </EProp>
        <EProp label="Link Color" data-id="element-494">
          <div className="flex gap-2" data-id="element-495">
            <input type="color" value={styles.linkColor} onChange={e => onChange({
            ...styles,
            linkColor: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-496" />
            <input type="text" value={styles.linkColor} onChange={e => onChange({
            ...styles,
            linkColor: e.target.value
          })} className="prop-input flex-1 font-mono text-xs" data-id="element-497" />
          </div>
        </EProp>
      </div>
    </div>;
}
function EProp({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-498">
      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-499">
        {label}
      </label>
      {children}
    </div>;
}
function EmailImageBlockProperties({
  block,
  onChange,
  onBlur
}: {
  block: EmailBlock;
  onChange: (updates: Partial<EmailBlock>) => void;
  onBlur: () => void;
}) {
  const {
    currentWorkspace
  } = useWorkspace();
  const team = currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaImages, setMediaImages] = useState<MediaLibraryRecord[]>([]);
  const [isLoadingMedia, setIsLoadingMedia] = useState(false);
  const [mediaSearch, setMediaSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'source' | 'layout' | 'effects'>('source');
  const isImageText = block.type === 'image_text';
  const srcProp = isImageText ? 'imageTextImageSrc' : 'src';
  const altProp = isImageText ? 'imageTextImageAlt' : 'alt';
  const widthProp = isImageText ? 'imageTextImageWidth' : 'imageWidth';
  const loadMedia = async () => {
    if (mediaImages.length > 0) return;
    setIsLoadingMedia(true);
    try {
      const all = await mediaLibraryApi.list();
      const teamFiltered = all.filter(img => img.Team === team || img.Team === currentWorkspace.name);
      setMediaImages(teamFiltered);
    } catch (err) {
      console.warn('Failed to load media library:', err);
    } finally {
      setIsLoadingMedia(false);
    }
  };
  const filteredMedia = mediaImages.filter(img => {
    if (!mediaSearch) return true;
    const q = mediaSearch.toLowerCase();
    return (img.Image_Name || '').toLowerCase().includes(q) || (img.Category || '').toLowerCase().includes(q);
  });
  const tabs = [{
    id: 'source' as const,
    label: 'Source',
    icon: ImageIcon
  }, {
    id: 'layout' as const,
    label: 'Layout',
    icon: SlidersHorizontalIcon
  }, {
    id: 'effects' as const,
    label: 'Effects',
    icon: SparklesIcon
  }];
  return <>
      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg mb-4" data-id="element-500">
        {tabs.map(t => {
        const Icon = t.icon;
        return <button key={t.id} onClick={() => setActiveTab(t.id)} className={`flex-1 flex items-center justify-center gap-1 py-1.5 text-[10px] font-medium rounded-md transition-colors ${activeTab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-501">
              <Icon className="h-3 w-3" data-id="element-502" />
              {t.label}
            </button>;
      })}
      </div>

      {activeTab === 'source' && <div className="space-y-4" data-id="element-503">
          <EProp label="Image URL" data-id="element-504">
            <input type="url" value={(block as any)[srcProp] || ''} onChange={e => onChange({
          [srcProp]: e.target.value
        })} onBlur={onBlur} placeholder="https://..." className="prop-input" data-id="element-505" />
          </EProp>

          <div data-id="element-506">
            <button onClick={() => {
          setShowMediaPicker(!showMediaPicker);
          if (!showMediaPicker) loadMedia();
        }} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors" data-id="element-507">
              <ImagePlusIcon className="h-3.5 w-3.5" data-id="element-508" />
              Choose from Media Library
            </button>

            {showMediaPicker && <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden bg-white" data-id="element-509">
                <div className="p-2 border-b border-gray-100" data-id="element-510">
                  <div className="relative" data-id="element-511">
                    <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" data-id="element-512" />
                    <input type="text" value={mediaSearch} onChange={e => setMediaSearch(e.target.value)} placeholder="Search images..." className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-513" />
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto p-2" data-id="element-514">
                  {isLoadingMedia ? <div className="flex items-center justify-center py-6" data-id="element-515">
                      <Loader2Icon className="h-5 w-5 text-gray-400 animate-spin" data-id="element-516" />
                    </div> : filteredMedia.length === 0 ? <div className="text-center py-6" data-id="element-517">
                      <ImageIcon className="h-6 w-6 text-gray-300 mx-auto mb-1" data-id="element-518" />
                      <p className="text-[10px] text-gray-400" data-id="element-519">
                        {mediaSearch ? 'No images match your search' : 'No images in library'}
                      </p>
                    </div> : <div className="grid grid-cols-3 gap-1.5" data-id="element-520">
                      {filteredMedia.map(img => <button key={img.ID} onClick={() => {
                onChange({
                  [srcProp]: img.Image_URL,
                  [altProp]: img.Image_Name || img.Description || ''
                });
                onBlur();
                setShowMediaPicker(false);
                setMediaSearch('');
              }} className={`relative group rounded-md overflow-hidden border-2 transition-all aspect-square ${(block as any)[srcProp] === img.Image_URL ? 'border-blue-500 ring-1 ring-blue-500' : 'border-transparent hover:border-blue-300'}`} data-id="element-521">
                          <img src={img.Image_URL} alt={img.Image_Name} className="w-full h-full object-cover" onError={e => {
                  ;
                  (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><rect fill="%23f3f4f6" width="40" height="40"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="10">?</text></svg>';
                }} data-id="element-522" />
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-523">
                            <p className="text-[9px] text-white truncate font-medium" data-id="element-524">
                              {img.Image_Name}
                            </p>
                          </div>
                        </button>)}
                    </div>}
                </div>
              </div>}
          </div>

          <EProp label="Alt Text" data-id="element-525">
            <input type="text" value={(block as any)[altProp] || ''} onChange={e => onChange({
          [altProp]: e.target.value
        })} onBlur={onBlur} className="prop-input" placeholder="Describe the image..." data-id="element-526" />
          </EProp>

          {!isImageText && <EProp label="Link URL" data-id="element-527">
              <input type="url" value={block.href || ''} onChange={e => onChange({
          href: e.target.value
        })} onBlur={onBlur} className="prop-input" placeholder="Optional link" data-id="element-528" />
            </EProp>}

          {(block as any)[srcProp] && <div className="rounded-lg border border-gray-200 overflow-hidden mt-2" data-id="element-529">
              <img src={(block as any)[srcProp]} alt={(block as any)[altProp] || ''} className="w-full h-24 object-contain bg-gray-50" onError={e => {
          ;
          (e.target as HTMLImageElement).style.display = 'none';
        }} data-id="element-530" />
            </div>}

          {isImageText && <>
              <div className="h-px bg-gray-200 my-4" data-id="element-531" />
              <EProp label="Text Content" data-id="element-532">
                <textarea value={block.imageTextContent || ''} onChange={e => onChange({
            imageTextContent: e.target.value
          })} onBlur={onBlur} rows={4} className="prop-input text-sm" data-id="element-533" />
              </EProp>
              <EProp label="Font Size" data-id="element-534">
                <input type="number" value={block.fontSize || 16} onChange={e => onChange({
            fontSize: Number(e.target.value)
          })} onBlur={onBlur} className="prop-input" data-id="element-535" />
              </EProp>
              <EProp label="Text Color" data-id="element-536">
                <div className="flex gap-2" data-id="element-537">
                  <input type="color" value={block.color || '#333333'} onChange={e => onChange({
              color: e.target.value
            })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-538" />
                  <input type="text" value={block.color || '#333333'} onChange={e => onChange({
              color: e.target.value
            })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-539" />
                </div>
              </EProp>
            </>}
        </div>}

      {activeTab === 'layout' && <div className="space-y-4" data-id="element-540">
          {isImageText && <EProp label="Layout" data-id="element-541">
              <select value={block.imageTextLayout || 'image-left'} onChange={e => {
          onChange({
            imageTextLayout: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-542">
                <option value="image-left" data-id="element-543">Image Left</option>
                <option value="image-right" data-id="element-544">Image Right</option>
              </select>
            </EProp>}

          <EProp label="Alignment" data-id="element-545">
            <div className="grid grid-cols-3 gap-1" data-id="element-546">
              {(['left', 'center', 'right'] as const).map(align => <button key={align} onClick={() => {
            onChange({
              imageAlign: align
            });
            onBlur();
          }} className={`py-1.5 text-[10px] font-medium rounded-md border transition-colors capitalize ${(block.imageAlign || 'center') === align ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`} data-id="element-547">
                  {align}
                </button>)}
            </div>
          </EProp>

          <EProp label="Sizing Mode" data-id="element-548">
            <select value={block.imageSizing || 'maxWidth'} onChange={e => {
          onChange({
            imageSizing: e.target.value as 'natural' | 'maxWidth' | 'custom'
          });
          onBlur();
        }} className="prop-input" data-id="element-549">
              <option value="natural" data-id="element-550">Natural (no constraints)</option>
              <option value="maxWidth" data-id="element-551">Max Width % (responsive)</option>
              <option value="custom" data-id="element-552">Custom dimensions</option>
            </select>
          </EProp>

          {(block.imageSizing || 'maxWidth') === 'maxWidth' && <EProp label="Max Width" data-id="element-553">
              <div className="flex items-center gap-2" data-id="element-554">
                <input type="range" min={10} max={100} value={block.imageMaxWidth || (block as any)[widthProp] || (isImageText ? 50 : 100)} onChange={e => {
            onChange({
              imageMaxWidth: Number(e.target.value),
              [widthProp]: Number(e.target.value)
            });
          }} onMouseUp={onBlur} className="flex-1" data-id="element-555" />
                <span className="text-xs text-gray-500 w-10 text-right" data-id="element-556">
                  {block.imageMaxWidth || (block as any)[widthProp] || (isImageText ? 50 : 100)}
                  %
                </span>
              </div>
            </EProp>}

          {block.imageSizing === 'custom' && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-557">
                <EProp label="Width (px)" data-id="element-558">
                  <input type="number" value={block.imageHeight || ''} onChange={e => onChange({
              imageHeight: e.target.value ? Number(e.target.value) : 0
            })} onBlur={onBlur} min={0} placeholder="auto" className="prop-input" data-id="element-559" />
                </EProp>
                <EProp label="Height (px)" data-id="element-560">
                  <input type="number" value={block.imageMaxHeight || ''} onChange={e => onChange({
              imageMaxHeight: e.target.value ? Number(e.target.value) : 0
            })} onBlur={onBlur} min={0} placeholder="auto" className="prop-input" data-id="element-561" />
                </EProp>
              </div>
            </>}

          {block.imageSizing === 'natural' && <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5" data-id="element-562">
              <p className="text-[10px] text-amber-700 leading-relaxed" data-id="element-563">
                <strong data-id="element-564">Natural:</strong> The image will render at its original
                dimensions with no constraints.
              </p>
            </div>}

          <EProp label="Object Fit" data-id="element-565">
            <select value={block.imageObjectFit || 'contain'} onChange={e => {
          onChange({
            imageObjectFit: e.target.value
          });
          onBlur();
        }} className="prop-input" data-id="element-566">
              <option value="contain" data-id="element-567">Contain (fit inside)</option>
              <option value="cover" data-id="element-568">Cover (fill & crop)</option>
              <option value="fill" data-id="element-569">Fill (stretch)</option>
              <option value="none" data-id="element-570">None (natural size)</option>
            </select>
          </EProp>

          <EProp label="Border Radius" data-id="element-571">
            <div className="flex items-center gap-2" data-id="element-572">
              <input type="range" min={0} max={50} value={block.imageBorderRadius || 0} onChange={e => onChange({
            imageBorderRadius: Number(e.target.value)
          })} onMouseUp={onBlur} className="flex-1" data-id="element-573" />
              <span className="text-xs text-gray-500 w-10 text-right" data-id="element-574">
                {block.imageBorderRadius || 0}px
              </span>
            </div>
          </EProp>
        </div>}

      {activeTab === 'effects' && <div className="space-y-4" data-id="element-575">
          <EProp label="Opacity" data-id="element-576">
            <div className="flex items-center gap-2" data-id="element-577">
              <input type="range" min={10} max={100} value={block.imageOpacity ?? 100} onChange={e => onChange({
            imageOpacity: Number(e.target.value)
          })} onMouseUp={onBlur} className="flex-1" data-id="element-578" />
              <span className="text-xs text-gray-500 w-10 text-right" data-id="element-579">
                {block.imageOpacity ?? 100}%
              </span>
            </div>
          </EProp>

          <EProp label="Shadow" data-id="element-580">
            <div className="grid grid-cols-4 gap-1" data-id="element-581">
              {(['none', 'sm', 'md', 'lg'] as const).map(s => <button key={s} onClick={() => {
            onChange({
              imageShadow: s
            });
            onBlur();
          }} className={`py-1.5 text-[10px] font-medium rounded-md border transition-colors capitalize ${(block.imageShadow || 'none') === s ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`} data-id="element-582">
                  {s === 'none' ? 'Off' : s.toUpperCase()}
                </button>)}
            </div>
          </EProp>
        </div>}
    </>;
}
function EmailTableBlockProperties({
  block,
  onChange,
  onBlur
}: {
  block: EmailBlock;
  onChange: (updates: Partial<EmailBlock>) => void;
  onBlur: () => void;
}) {
  const [activeTab, setActiveTab] = useState<'visual' | 'html'>('visual');
  // Initialize tableData if missing
  const td = block.tableData || {
    rows: [['Cell 1', 'Cell 2'], ['Cell 3', 'Cell 4']],
    hasHeader: false,
    headerBgColor: '#f3f4f6',
    headerTextColor: '#111827',
    borderColor: '#e5e7eb',
    stripedRows: false,
    stripedColor: '#f9fafb',
    cellAlign: 'left' as const
  };
  const updateTableData = (updates: Partial<typeof td>) => {
    const newTd = {
      ...td,
      ...updates
    };
    // Generate HTML from the new table data
    let html = '';
    newTd.rows.forEach((row, rIdx) => {
      const isHeader = newTd.hasHeader && rIdx === 0;
      const isStriped = newTd.stripedRows && rIdx % 2 === 1 && !isHeader;
      let rowStyle = '';
      if (isHeader) {
        rowStyle = `background-color:${newTd.headerBgColor};color:${newTd.headerTextColor};font-weight:bold;`;
      } else if (isStriped) {
        rowStyle = `background-color:${newTd.stripedColor};`;
      }
      html += `<tr style="${rowStyle}">`;
      row.forEach(cell => {
        const tag = isHeader ? 'th' : 'td';
        html += `<${tag} style="padding:10px;border:1px solid ${newTd.borderColor};text-align:${newTd.cellAlign};">${cell}</${tag}>`;
      });
      html += `</tr>`;
    });
    onChange({
      tableData: newTd,
      content: html
    });
  };
  const addRow = () => {
    const cols = td.rows[0]?.length || 2;
    const newRow = Array(cols).fill('New Cell');
    updateTableData({
      rows: [...td.rows, newRow]
    });
    onBlur();
  };
  const removeRow = (idx: number) => {
    if (td.rows.length <= 1) return;
    const newRows = [...td.rows];
    newRows.splice(idx, 1);
    updateTableData({
      rows: newRows
    });
    onBlur();
  };
  const addCol = () => {
    const newRows = td.rows.map(row => [...row, 'New Cell']);
    updateTableData({
      rows: newRows
    });
    onBlur();
  };
  const removeCol = (idx: number) => {
    if ((td.rows[0]?.length || 0) <= 1) return;
    const newRows = td.rows.map(row => {
      const newRow = [...row];
      newRow.splice(idx, 1);
      return newRow;
    });
    updateTableData({
      rows: newRows
    });
    onBlur();
  };
  const updateCell = (rIdx: number, cIdx: number, val: string) => {
    const newRows = [...td.rows];
    newRows[rIdx] = [...newRows[rIdx]];
    newRows[rIdx][cIdx] = val;
    updateTableData({
      rows: newRows
    });
  };
  return <>
      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg mb-4" data-id="element-583">
        <button onClick={() => setActiveTab('visual')} className={`flex-1 py-1.5 text-[10px] font-medium rounded-md transition-colors ${activeTab === 'visual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-584">
          Visual Editor
        </button>
        <button onClick={() => setActiveTab('html')} className={`flex-1 py-1.5 text-[10px] font-medium rounded-md transition-colors ${activeTab === 'html' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-585">
          Raw HTML
        </button>
      </div>

      {activeTab === 'visual' && <div className="space-y-4" data-id="element-586">
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 p-2 overflow-x-auto" data-id="element-587">
            <div className="flex gap-2 mb-2" data-id="element-588">
              <button onClick={addRow} className="px-2 py-1 bg-white border border-gray-200 rounded text-[10px] font-medium text-gray-700 hover:bg-gray-50" data-id="element-589">
                + Add Row
              </button>
              <button onClick={addCol} className="px-2 py-1 bg-white border border-gray-200 rounded text-[10px] font-medium text-gray-700 hover:bg-gray-50" data-id="element-590">
                + Add Column
              </button>
            </div>

            <div className="inline-block min-w-full" data-id="element-591">
              <table className="w-full border-collapse bg-white shadow-sm" data-id="element-592">
                <tbody data-id="element-593">
                  {td.rows.map((row, rIdx) => <tr key={rIdx} className="group" data-id="element-594">
                      {row.map((cell, cIdx) => <td key={cIdx} className="border border-gray-200 relative p-0" data-id="element-595">
                          <input type="text" value={cell} onChange={e => updateCell(rIdx, cIdx, e.target.value)} onBlur={onBlur} className="w-full h-full p-2 text-xs border-none focus:ring-1 focus:ring-blue-500" style={{
                    backgroundColor: td.hasHeader && rIdx === 0 ? td.headerBgColor : td.stripedRows && rIdx % 2 === 1 ? td.stripedColor : 'transparent',
                    color: td.hasHeader && rIdx === 0 ? td.headerTextColor : 'inherit',
                    fontWeight: td.hasHeader && rIdx === 0 ? 'bold' : 'normal',
                    textAlign: td.cellAlign
                  }} data-id="element-596" />
                          {rIdx === 0 && td.rows[0].length > 1 && <button onClick={() => removeCol(cIdx)} className="absolute -top-2 -right-2 w-4 h-4 bg-red-100 text-red-600 rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center text-[10px] hover:bg-red-200 z-10" title="Remove column" data-id="element-597">
                              ×
                            </button>}
                        </td>)}
                      <td className="w-6 p-0 border-none bg-transparent" data-id="element-598">
                        {td.rows.length > 1 && <button onClick={() => removeRow(rIdx)} className="w-4 h-4 ml-1 bg-red-100 text-red-600 rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center text-[10px] hover:bg-red-200" title="Remove row" data-id="element-599">
                            ×
                          </button>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-600">
            <EProp label="Header Row" data-id="element-601">
              <select value={td.hasHeader ? 'yes' : 'no'} onChange={e => {
            updateTableData({
              hasHeader: e.target.value === 'yes'
            });
            onBlur();
          }} className="prop-input" data-id="element-602">
                <option value="yes" data-id="element-603">Yes</option>
                <option value="no" data-id="element-604">No</option>
              </select>
            </EProp>

            <EProp label="Striped Rows" data-id="element-605">
              <select value={td.stripedRows ? 'yes' : 'no'} onChange={e => {
            updateTableData({
              stripedRows: e.target.value === 'yes'
            });
            onBlur();
          }} className="prop-input" data-id="element-606">
                <option value="yes" data-id="element-607">Yes</option>
                <option value="no" data-id="element-608">No</option>
              </select>
            </EProp>
          </div>

          {td.hasHeader && <div className="grid grid-cols-2 gap-3" data-id="element-609">
              <EProp label="Header BG" data-id="element-610">
                <div className="flex gap-2" data-id="element-611">
                  <input type="color" value={td.headerBgColor} onChange={e => updateTableData({
              headerBgColor: e.target.value
            })} onBlur={onBlur} className="h-8 w-8 rounded border cursor-pointer" data-id="element-612" />
                  <input type="text" value={td.headerBgColor} onChange={e => updateTableData({
              headerBgColor: e.target.value
            })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-613" />
                </div>
              </EProp>
              <EProp label="Header Text" data-id="element-614">
                <div className="flex gap-2" data-id="element-615">
                  <input type="color" value={td.headerTextColor} onChange={e => updateTableData({
              headerTextColor: e.target.value
            })} onBlur={onBlur} className="h-8 w-8 rounded border cursor-pointer" data-id="element-616" />
                  <input type="text" value={td.headerTextColor} onChange={e => updateTableData({
              headerTextColor: e.target.value
            })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-617" />
                </div>
              </EProp>
            </div>}

          <div className="grid grid-cols-2 gap-3" data-id="element-618">
            <EProp label="Border Color" data-id="element-619">
              <div className="flex gap-2" data-id="element-620">
                <input type="color" value={td.borderColor} onChange={e => updateTableData({
              borderColor: e.target.value
            })} onBlur={onBlur} className="h-8 w-8 rounded border cursor-pointer" data-id="element-621" />
                <input type="text" value={td.borderColor} onChange={e => updateTableData({
              borderColor: e.target.value
            })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-622" />
              </div>
            </EProp>

            {td.stripedRows && <EProp label="Stripe Color" data-id="element-623">
                <div className="flex gap-2" data-id="element-624">
                  <input type="color" value={td.stripedColor} onChange={e => updateTableData({
              stripedColor: e.target.value
            })} onBlur={onBlur} className="h-8 w-8 rounded border cursor-pointer" data-id="element-625" />
                  <input type="text" value={td.stripedColor} onChange={e => updateTableData({
              stripedColor: e.target.value
            })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-626" />
                </div>
              </EProp>}
          </div>

          <EProp label="Cell Alignment" data-id="element-627">
            <select value={td.cellAlign} onChange={e => {
          updateTableData({
            cellAlign: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-628">
              <option value="left" data-id="element-629">Left</option>
              <option value="center" data-id="element-630">Center</option>
              <option value="right" data-id="element-631">Right</option>
            </select>
          </EProp>

          <EProp label="Font Size" data-id="element-632">
            <input type="number" value={block.fontSize || 14} onChange={e => onChange({
          fontSize: Number(e.target.value)
        })} onBlur={onBlur} className="prop-input" data-id="element-633" />
          </EProp>
        </div>}

      {activeTab === 'html' && <div className="space-y-4" data-id="element-634">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 mb-2" data-id="element-635">
            <p className="text-[10px] text-amber-700 leading-relaxed" data-id="element-636">
              <strong data-id="element-637">Warning:</strong> Editing the raw HTML directly will
              override the visual editor. If you switch back to the visual
              editor and make changes, your custom HTML will be overwritten.
            </p>
          </div>
          <EProp label="Table HTML Content (<tbody>)" data-id="element-638">
            <textarea value={block.content || ''} onChange={e => onChange({
          content: e.target.value
        })} onBlur={onBlur} rows={8} className="prop-input text-sm font-mono" data-id="element-639" />
          </EProp>
          <EProp label="Font Size" data-id="element-640">
            <input type="number" value={block.fontSize || 14} onChange={e => onChange({
          fontSize: Number(e.target.value)
        })} onBlur={onBlur} className="prop-input" data-id="element-641" />
          </EProp>
        </div>}
    </>;
}